import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface CommandItem {
  id: string;
  label: string;
  category: string;
  action: () => void;
  keywords?: string[];
}

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const commands: CommandItem[] = [
    {
      id: 'nav-command-center',
      label: 'Go to Command Center',
      category: 'Navigation',
      action: () => navigate('/console'),
      keywords: ['home', 'dashboard', 'overview'],
    },
    {
      id: 'nav-outcomes-registry',
      label: 'Go to Outcomes Registry',
      category: 'Navigation',
      action: () => navigate('/console/outcomes-registry'),
      keywords: ['patients', 'registry', 'outcomes'],
    },
    {
      id: 'nav-lots',
      label: 'Go to Lots',
      category: 'Navigation',
      action: () => navigate('/console/lots'),
      keywords: ['cell lines', 'inventory', 'vials'],
    },
    {
      id: 'nav-studies',
      label: 'Go to Studies',
      category: 'Navigation',
      action: () => navigate('/console/studies'),
      keywords: ['trials', 'protocols', 'clinical'],
    },
    {
      id: 'nav-providers',
      label: 'Go to Providers',
      category: 'Navigation',
      action: () => navigate('/console/providers'),
      keywords: ['clinics', 'care team'],
    },
    {
      id: 'nav-investigators',
      label: 'Go to Investigators',
      category: 'Navigation',
      action: () => navigate('/console/investigators'),
      keywords: ['researchers', 'protocol owners'],
    },
    {
      id: 'nav-intelligence',
      label: 'Go to Intelligence',
      category: 'Navigation',
      action: () => navigate('/console/intelligence'),
      keywords: ['reports', 'insights', 'analytics', 'export'],
    },
    {
      id: 'nav-consent-access',
      label: 'Go to Consent & Access',
      category: 'Navigation',
      action: () => navigate('/console/consent-access'),
      keywords: ['permissions', 'access', 'consent'],
    },
    {
      id: 'nav-audit',
      label: 'Go to Audit',
      category: 'Navigation',
      action: () => navigate('/console/audit'),
      keywords: ['audit log', 'compliance'],
    },
    {
      id: 'nav-adverse-events',
      label: 'Go to Adverse Events',
      category: 'Navigation',
      action: () => navigate('/console/adverse-events'),
      keywords: ['qc', 'safety', 'event capture'],
    },
    {
      id: 'nav-compare',
      label: 'Go to Compare',
      category: 'Navigation',
      action: () => navigate('/console/compare'),
      keywords: ['diff', 'side by side'],
    },
    {
      id: 'nav-ai-copilot',
      label: 'Go to AI Co-pilot',
      category: 'Navigation',
      action: () => navigate('/console/ai-copilot'),
      keywords: ['assistant', 'ai', 'copilot'],
    },
    {
      id: 'nav-settings',
      label: 'Go to Settings',
      category: 'Navigation',
      action: () => navigate('/console/settings'),
      keywords: ['admin', 'configuration'],
    },
    {
      id: 'nav-c15',
      label: 'Lot: C15 (hiPSC)',
      category: 'Lots',
      action: () => navigate('/console/lots'),
      keywords: ['cell line', 'hipsc'],
    },
    {
      id: 'nav-c20',
      label: 'Lot: C20 (hiPSC)',
      category: 'Lots',
      action: () => navigate('/console/lots'),
      keywords: ['cell line', 'hipsc'],
    },
  ];

  const filtered = query
    ? commands.filter(
        (cmd) =>
          fuzzyMatch(query, cmd.label) ||
          fuzzyMatch(query, cmd.category) ||
          cmd.keywords?.some((kw) => fuzzyMatch(query, kw)),
      )
    : commands;

  const execute = useCallback((item: CommandItem) => {
    item.action();
    setOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  // Cmd+K / Ctrl+K to toggle
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Keyboard navigation inside palette
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[selectedIndex];
        if (item) execute(item);
      } else if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }
    },
    [filtered, selectedIndex, execute],
  );

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const items = listRef.current.querySelectorAll('[data-command-item]');
      const selected = items[selectedIndex];
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '20vh',
        background: 'rgba(0, 0, 0, 0.5)',
      }}
      onClick={() => {
        setOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }}
      role="dialog"
      aria-label="Command palette"
      aria-modal="true"
    >
      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          background: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)' }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search Lab Console commands..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search commands"
            style={{
              width: '100%',
              height: '40px',
              background: 'var(--color-bg-input)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '0 var(--space-4)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-body)',
              fontFamily: 'var(--font-body)',
              outline: 'none',
            }}
          />
        </div>

        {/* Results */}
        <div
          ref={listRef}
          style={{ maxHeight: '320px', overflowY: 'auto', padding: 'var(--space-2)' }}
          role="listbox"
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: 'var(--space-6)',
                textAlign: 'center',
                color: 'var(--color-text-faint)',
                fontSize: 'var(--text-caption)',
              }}
            >
              No results found
            </div>
          ) : (
            filtered.map((item, i) => (
              <div
                key={item.id}
                data-command-item
                role="option"
                aria-selected={i === selectedIndex}
                onClick={() => execute(item)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-3) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  background:
                    i === selectedIndex ? 'var(--color-accent-primary-subtle)' : 'transparent',
                  transition: 'background var(--duration-fast)',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--text-caption)',
                    color: i === selectedIndex ? 'var(--color-text)' : 'var(--color-text-muted)',
                    fontWeight: i === selectedIndex ? 500 : 400,
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-faint)',
                  }}
                >
                  {item.category}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--space-2) var(--space-4)',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            gap: 'var(--space-4)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-faint)',
          }}
        >
          <span>
            <kbd style={kbdStyle}>↑↓</kbd> Navigate
          </span>
          <span>
            <kbd style={kbdStyle}>↵</kbd> Select
          </span>
          <span>
            <kbd style={kbdStyle}>esc</kbd> Close
          </span>
        </div>
      </div>
    </div>
  );
}

const kbdStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '20px',
  height: '20px',
  padding: '0 4px',
  background: 'var(--color-bg-elevated)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  fontSize: '10px',
  fontFamily: 'var(--font-mono)',
  marginRight: '4px',
};
